import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { getToken, getAuthUser, isAuthenticated } from "../auth";

const API = "https://api.vyapakprint.com/api";

const Wallet = () => {
  const [wallet, setWallet] = useState(0);
  const [txns, setTxns] = useState([]);
  const [distributionId, setDistributionId] = useState(null);
  const [accountName, setAccountName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const auth = useMemo(() => getAuthUser(), []);
  const token = useMemo(() => getToken(), []);

  useEffect(() => {
    const fetchWalletData = async () => {
      try {
        setLoading(true);
        setError("");

        if (!token || !isAuthenticated()) {
          throw new Error("Please log in again.");
        }

        const storedUser = auth?.storedUser || null;

        const email = auth?.email || storedUser?.email || null;

        const safeDistributionId =
          auth?.distributionId ||
          auth?.authUserId ||
          storedUser?.distributionId ||
          storedUser?.id ||
          storedUser?._id ||
          null;

        if (storedUser) {
          setAccountName(
            storedUser?.businessName ||
              storedUser?.contactPerson ||
              "Business Account",
          );
          setWallet(Number(storedUser?.wallet || 0));
          setDistributionId(
            storedUser?.distributionId ||
              storedUser?.id ||
              storedUser?._id ||
              null,
          );
        }

        const res = await fetch(`${API}/distribution/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const raw = await res.json();

        let list = [];
        if (Array.isArray(raw)) {
          list = raw;
        } else if (Array.isArray(raw?.data)) {
          list = raw.data;
        } else if (Array.isArray(raw?.distributions)) {
          list = raw.distributions;
        }

        const matchedUser =
          list.find(
            (item) =>
              safeDistributionId &&
              String(item?._id || item?.id) === String(safeDistributionId),
          ) ||
          list.find(
            (item) =>
              email &&
              String(item?.email || "").toLowerCase() ===
                String(email).toLowerCase(),
          ) ||
          storedUser;

        if (!matchedUser) {
          throw new Error("Distribution account not found.");
        }

        const normalizedUser = {
          id: matchedUser?._id || matchedUser?.id || null,
          _id: matchedUser?._id || matchedUser?.id || null,
          distributionId: matchedUser?._id || matchedUser?.id || null,
          email: matchedUser?.email || email || "",
          businessName:
            matchedUser?.businessName || storedUser?.businessName || "",
          contactPerson:
            matchedUser?.contactPerson || storedUser?.contactPerson || "",
          wallet: Number(matchedUser?.wallet || 0),
          transactionHistory: Array.isArray(matchedUser?.transactionHistory)
            ? matchedUser.transactionHistory
            : [],
        };

        setDistributionId(normalizedUser.distributionId);
        setWallet(normalizedUser.wallet);
        setAccountName(
          normalizedUser.businessName ||
            normalizedUser.contactPerson ||
            "Business Account",
        );

        const sortedTxns = [...normalizedUser.transactionHistory].sort(
          (a, b) => {
            const aTime = a?.date ? new Date(a.date).getTime() : 0;
            const bTime = b?.date ? new Date(b.date).getTime() : 0;
            return bTime - aTime;
          },
        );

        setTxns(sortedTxns);

        localStorage.setItem(
          "user",
          JSON.stringify({
            id: normalizedUser.id,
            _id: normalizedUser._id,
            distributionId: normalizedUser.distributionId,
            email: normalizedUser.email,
            businessName: normalizedUser.businessName,
            contactPerson: normalizedUser.contactPerson,
            wallet: normalizedUser.wallet,
          }),
        );

        localStorage.setItem(
          "distributionUser",
          JSON.stringify({
            id: normalizedUser.id,
            _id: normalizedUser._id,
            distributionId: normalizedUser.distributionId,
            email: normalizedUser.email,
            businessName: normalizedUser.businessName,
            contactPerson: normalizedUser.contactPerson,
            wallet: normalizedUser.wallet,
          }),
        );
      } catch (err) {
        console.error("Wallet fetch error:", err);
        setError(err?.message || "Failed to load wallet data");
      } finally {
        setLoading(false);
      }
    };

    fetchWalletData();
  }, [auth, token]);

  const exportToExcel = () => {
    const rows = txns.map((t) => {
      const transactionType = String(t?.transactionType || "").toLowerCase();
      const isDebit = transactionType === "debit";

      return {
        Date: t?.date ? new Date(t.date).toLocaleString("en-IN") : "—",
        Type: t?.transactionType || "—",
        Amount: `${isDebit ? "-" : "+"}${Number(t?.amount || 0).toFixed(2)}`,
        PreviousBalance: Number(t?.previousBalance || 0).toFixed(2),
        NewBalance: Number(t?.newBalance || 0).toFixed(2),
        Remark: t?.remark || "—",
        ReceiptID: t?.receiptId || "—",
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transactions");
    XLSX.writeFile(wb, "wallet-transactions.xlsx");
  };

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return <div className="min-h-screen p-6">Loading wallet...</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-600">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto min-h-screen p-4 md:p-6">
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Wallet</h1>
          <p className="mt-1 text-sm text-gray-500">
            {accountName || "Business Account"}
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Link to="/add-funds">
            <button className="rounded-lg bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700">
              Add Funds
            </button>
          </Link>

          <button
            onClick={exportToExcel}
            className="rounded-lg bg-green-600 px-4 py-2 text-white shadow hover:bg-green-700"
          >
            Export to Excel
          </button>
        </div>
      </div>

      <div className="mb-6 rounded-lg border bg-white p-6 shadow">
        <h2 className="mb-4 text-xl font-semibold">Wallet Balance</h2>
        <p className="text-3xl font-bold text-green-600">
          ₹{Number(wallet).toFixed(2)}
        </p>
        <p className="mt-2 text-gray-600">Total available balance</p>

        {distributionId && (
          <p className="mt-3 text-xs text-gray-400">
            Account ID: {distributionId}
          </p>
        )}
      </div>

      <div className="overflow-auto rounded-lg border bg-white p-4 shadow md:p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-xl font-semibold">Transaction History</h2>
          <span className="text-sm text-gray-500">
            {txns.length} transaction{txns.length !== 1 ? "s" : ""}
          </span>
        </div>

        <table className="min-w-[1000px] w-full text-center text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3">Date &amp; Time</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Amount (₹)</th>
              <th className="px-4 py-3">Previous</th>
              <th className="px-4 py-3">New</th>
              <th className="px-4 py-3">Remark</th>
              <th className="px-4 py-3">Receipt ID</th>
            </tr>
          </thead>

          <tbody>
            {txns.length > 0 ? (
              txns.map((t, index) => {
                const type = String(t?.transactionType || "").toLowerCase();
                const isDebit = type === "debit";

                return (
                  <tr
                    key={
                      t?._id || t?.receiptId || `${t?.date || "txn"}-${index}`
                    }
                    className="border-t"
                  >
                    <td className="px-4 py-2">
                      {t?.date ? new Date(t.date).toLocaleString("en-IN") : "—"}
                    </td>

                    <td className="px-4 py-2 capitalize">
                      {t?.transactionType || "—"}
                    </td>

                    <td
                      className={`px-4 py-2 font-semibold ${
                        isDebit ? "text-red-600" : "text-green-600"
                      }`}
                    >
                      {isDebit ? "-" : "+"}
                      {Number(t?.amount || 0).toFixed(2)}
                    </td>

                    <td className="px-4 py-2">
                      {Number(t?.previousBalance || 0).toFixed(2)}
                    </td>

                    <td className="px-4 py-2">
                      {Number(t?.newBalance || 0).toFixed(2)}
                    </td>

                    <td className="px-4 py-2">{t?.remark || "—"}</td>

                    <td className="px-4 py-2">{t?.receiptId || "—"}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="7" className="py-6 text-gray-500">
                  No transactions yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Wallet;
